import React, { createContext, useContext } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation } from "react-router-dom";

import { dashboardConfig } from "../../../../config/dashboard";
import { dashboardConfigWorkspace } from "../../../../config/dashboard-workspace";
import { useUser } from "../../../../api/hooks";
import { hasFeature } from "../../../feature/hasFeature";
import { getStoredAccessToken } from "@/components/auth/tokenManager";

type WorkspaceType = "default" | "workspaceB";

interface WorkspaceContextType {
    workspace: WorkspaceType;
    config: any;
}

const WorkspaceContext = createContext<WorkspaceContextType | null>(null);

export const WorkspaceProvider = ({ children }: { children: React.ReactNode }) => {
    const location = useLocation();
    const { isAuthenticated, isLoading: authLoading } = useAuth0();

    const tokenReady = !!getStoredAccessToken();
    const canLoadUser = !authLoading && isAuthenticated && tokenReady;

    const { data: user, isLoading: userLoading } = useUser({
        enabled: canLoadUser,
    });

    if (authLoading || (canLoadUser && userLoading)) {
        return null; 
    }

    const canUseWorkspace = canLoadUser && hasFeature(user, "workspace:show");


    const workspace: WorkspaceType =
        location.pathname.startsWith("/dashboard/workspace")
            ? "workspaceB"
            : "default";

    const config =
        workspace === "workspaceB"
            ? dashboardConfigWorkspace
            : dashboardConfig;


    return (
        <WorkspaceContext.Provider value={{ workspace, config }}>
            {children}
        </WorkspaceContext.Provider>
    );
};

export const useWorkspace = () => {
    const ctx = useContext(WorkspaceContext);

    if (!ctx) {
        throw new Error("useWorkspace must be used inside WorkspaceProvider");
    }

    return ctx;
};
